import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Language } from '../contexts/LanguageContext';
interface LandingPageProps {
  onSelectLanguage: (lang: Language) => void;
  onConsularAccess?: (lang: Language) => void;
}
export function LandingPage({
  onSelectLanguage,
  onConsularAccess
}: LandingPageProps) {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <div className="fixed inset-0 z-50 flex flex-col items-center justify-start sm:justify-center overflow-y-auto overflow-x-hidden hero-bg" data-id="element-2572">
      {/* Decorative Orbs */}
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-haa-skyblue/15 rounded-full blur-3xl orb-1" data-id="element-2573" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-haa-midblue/12 rounded-full blur-3xl orb-2" data-id="element-2574" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-haa-blue/8 rounded-full blur-3xl orb-3" data-id="element-2575" />
      <div className="absolute top-1/3 right-1/3 w-48 h-48 bg-haa-skyblue/10 rounded-full blur-2xl orb-4" data-id="element-2576" />
      <div className="absolute bottom-1/3 left-1/3 w-56 h-56 bg-haa-midblue/10 rounded-full blur-2xl orb-5" data-id="element-2577" />

      {/* Content Container */}
      <motion.div variants={containerVariants} initial="hidden" animate="visible" className="relative z-10 flex flex-col items-center text-center px-4 w-full max-w-4xl pt-28 pb-12 sm:pt-32 sm:pb-0" data-id="element-2578">
        {/* Branding */}
        <motion.div variants={itemVariants} className="mb-8 sm:mb-12 flex flex-col items-center relative" data-id="element-2579">
          {/* Soft glow behind logo for contrast */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[140%] bg-haa-skyblue/5 rounded-full blur-3xl pointer-events-none" data-id="element-2580" />
          <img src="https://cdn.magicpatterns.com/uploads/b1GB8Z9aEvoE58p9BdX4qx/HonduranAmericanLogoNew.png" alt="Honduran American Association" className="w-64 sm:w-80 md:w-96 mb-4 relative z-10 drop-shadow-[0_2px_20px_rgba(0,63,135,0.15)]" data-id="element-2581" />
          <span className="font-nunito text-haa-midblue text-sm uppercase tracking-[0.3em] font-bold relative z-10" data-id="element-2582">
            Est. 2025
          </span>
        </motion.div>

        {/* Welcome Text */}
        <motion.div variants={itemVariants} className="mb-8 sm:mb-12" data-id="element-2583">
          <h2 className="font-bebas text-6xl sm:text-7xl md:text-8xl lg:text-9xl text-haa-navy tracking-wider leading-[0.85] mb-2" data-id="element-2584">
            Welcome
          </h2>
          <h2 className="font-bebas text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-haa-blue tracking-wider leading-[0.85]" data-id="element-2585">
            Bienvenidos
          </h2>
        </motion.div>

        {/* Language Buttons */}
        <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-center justify-center gap-6 w-full max-w-md sm:max-w-none" data-id="element-2586">
          <motion.button whileHover={{
          scale: 1.05,
          y: -4
        }} whileTap={{
          scale: 0.95
        }} onClick={() => onSelectLanguage('EN')} className="w-full sm:w-64 py-5 px-8 bg-haa-navy rounded-2xl shadow-[0_8px_30px_rgba(0,26,62,0.2)] hover:shadow-[0_20px_40px_rgba(0,26,62,0.3)] transition-shadow duration-300 flex flex-col items-center justify-center group border border-haa-navy/10" data-id="element-2587">
            <span className="font-bebas text-3xl text-white tracking-wider group-hover:text-haa-skyblue transition-colors" data-id="element-2588">
              English
            </span>
            <span className="font-nunito text-sm text-white/60 mt-1" data-id="element-2589">
              Continue in English
            </span>
          </motion.button>

          <motion.button whileHover={{
          scale: 1.05,
          y: -4
        }} whileTap={{
          scale: 0.95
        }} onClick={() => onSelectLanguage('ES')} className="w-full sm:w-64 py-5 px-8 bg-haa-blue rounded-2xl shadow-[0_8px_30px_rgba(0,63,135,0.2)] hover:shadow-[0_20px_40px_rgba(0,63,135,0.3)] transition-shadow duration-300 flex flex-col items-center justify-center group border border-haa-blue/10" data-id="element-2590">
            <span className="font-bebas text-3xl text-white tracking-wider group-hover:text-haa-skyblue transition-colors" data-id="element-2591">
              Español
            </span>
            <span className="font-nunito text-sm text-white/60 mt-1" data-id="element-2592">
              Continuar en Español
            </span>
          </motion.button>
        </motion.div>

        {/* Consular Affairs Quick Access */}
        <motion.div variants={itemVariants} className="mt-10 pb-12 flex flex-col items-center gap-3" data-id="element-2593">
          <p className="font-nunito text-haa-navy/40 text-xs uppercase tracking-[0.2em]" data-id="element-2594">
            Consular Services <span className="mx-1.5 opacity-50" data-id="element-2595">/</span>{' '}
            Servicios Consulares
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3" data-id="element-2596">
            <motion.button whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={() => onConsularAccess?.('EN')} className="inline-flex items-center gap-2 bg-haa-midblue/10 backdrop-blur-sm border border-haa-midblue/20 text-haa-navy font-nunito font-semibold text-sm px-6 py-3 rounded-full hover:bg-haa-midblue/20 hover:border-haa-midblue/40 transition-all duration-200" data-id="element-2597">
              <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2598">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" data-id="element-2599" />
                <polyline points="14 2 14 8 20 8" data-id="element-2600" />
                <line x1="16" y1="13" x2="8" y2="13" data-id="element-2601" />
                <line x1="16" y1="17" x2="8" y2="17" data-id="element-2602" />
              </svg>
              Consular Affairs in English
            </motion.button>
            <motion.button whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={() => onConsularAccess?.('ES')} className="inline-flex items-center gap-2 bg-haa-midblue/10 backdrop-blur-sm border border-haa-midblue/20 text-haa-navy font-nunito font-semibold text-sm px-6 py-3 rounded-full hover:bg-haa-midblue/20 hover:border-haa-midblue/40 transition-all duration-200" data-id="element-2603">
              <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2604">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" data-id="element-2605" />
                <polyline points="14 2 14 8 20 8" data-id="element-2606" />
                <line x1="16" y1="13" x2="8" y2="13" data-id="element-2607" />
                <line x1="16" y1="17" x2="8" y2="17" data-id="element-2608" />
              </svg>
              Asuntos Consulares en Español
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </div>;
}